// "Cảm xúc" sheet: who reacted to a post, filterable per emoji, loaded page by page.

import { h } from './dom';
import { api, type Emoji, type Reactor } from '../api/client';
import { avatar, sheet, skeleton, timeAgo } from './components';
import { REACTION_EMOJI, sortEmojiCounts } from './reaction-text';

const PAGE = 30;

/** `counts` = the post's per-emoji totals (for the chips); the list itself comes from the API. */
export function openReactors(postId: string, counts: Partial<Record<Emoji, number>>) {
  const chips = h('div.rx-chips', { role: 'tablist', 'aria-label': 'Lọc theo cảm xúc' });
  const listEl = h('div.rx-list', { role: 'list' });
  const foot = h('div.rx-foot');
  const box = h('div.rx', {}, chips, listEl, foot);
  let filter: Emoji | null = null;
  let rows: Reactor[] = [];
  let offset = 0;
  let done = false;
  let loading = false;

  const total = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0);
  const paintChips = () => {
    const chip = (key: Emoji | null, label: string) => h('button.rx-chip', {
      role: 'tab', 'aria-selected': String(filter === key), class: filter === key ? 'on' : '',
      onclick: () => { filter = key; paintChips(); paintRows(); },
    }, label);
    chips.replaceChildren(chip(null, total ? `Tất cả ${total}` : 'Tất cả'),
      ...sortEmojiCounts(counts).map(([e, n]) => chip(e, `${REACTION_EMOJI[e]} ${n}`)));
  };

  const paintRows = () => {
    const shown = filter ? rows.filter((r) => r.emoji === filter) : rows;
    listEl.replaceChildren(...shown.map((r) => h('a.urow.rx-row', { href: `#/u/${r.user.id}`, role: 'listitem', onclick: () => close() },
      h('span.rx-av', {}, avatar(r.user, 40), h('span.rx-badge', { 'aria-label': r.emoji }, REACTION_EMOJI[r.emoji] ?? '❤️')),
      h('div.umain', {}, h('b', {}, r.user.displayName), h('small', {}, timeAgo(r.at))))));
    if (done && !shown.length) listEl.replaceChildren(h('p.tiny', {}, 'Chưa có ai thả cảm xúc này.'));
    // a filter may hide everything loaded so far: keep loading while the sentinel is visible
    if (!done) io.observe(foot);
  };

  const load = async () => {
    if (loading || done) return;
    loading = true;
    foot.replaceChildren(skeleton('row', rows.length ? 1 : 4));
    try {
      const page = await api.reactors(postId, offset, PAGE);
      offset += page.length;
      rows = rows.concat(page);
      done = page.length < PAGE;
      foot.replaceChildren(done && rows.length ? h('p.tiny', {}, `${rows.length} người`) : '');
    } catch (e) {
      foot.replaceChildren(h('p.tiny', {}, (e as Error).message), h('button.secondary.small', { onclick: () => void load() }, 'Thử lại'));
      loading = false;
      return;
    }
    loading = false;
    paintRows();
  };

  const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) void load(); }, { root: null, rootMargin: '0px 0px 400px 0px' });
  // leaving the page (back button, links) closes the sheet
  const onNav = () => close();
  const close = sheet('Cảm xúc', box, { onClose: () => { io.disconnect(); removeEventListener('hashchange', onNav); } });
  addEventListener('hashchange', onNav);
  paintChips();
  void load();
  return close;
}
